import { generateClient } from 'aws-amplify/api';
import {
  Job,
  JobConnection,
  JobType,
  JobStatus,
  StepType,
  StepStatus,
  CreateJobInput,
  UpdateStepStatusInput,
  UploadCredentials
} from '../graphql/types';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';

const client = generateClient();

/**
 * Response from createJob mutation including upload credentials
 */
export interface CreateJobResponse {
  job: Job;
  uploadCredentials: UploadCredentials;
  uploadBucket: string;
  uploadPrefix: string;
}

/**
 * WorkflowService - Provides job-type-aware operations for jobs
 * 
 * Requirements: 2.2, 2.3, 3.1, 4.1, 9.5, 9.6
 */

/**
 * Creates a new job with the specified name and job type.
 * 
 * @param input - The job creation input containing name and jobType
 * @returns The created Job with upload credentials
 * 
 * Requirement 2.2: Create a new Job with the appropriate Job_Type
 * Requirement 2.3: Associate the new job with the current user's tenant
 */
export async function createJob(input: CreateJobInput): Promise<CreateJobResponse> {
  const response = await client.graphql({
    query: mutations.createJob,
    variables: { input }
  });
  return (response as any).data.createJob;
}

/**
 * Retrieves a job by ID and tenant.
 * 
 * @param jobId - The job ID
 * @param tenantId - The tenant ID
 * @returns The Job or null if not found
 * 
 * Requirement 9.6: Support resuming jobs from the last incomplete step
 */
export async function getJob(jobId: string, tenantId: string): Promise<Job | null> {
  const response = await client.graphql({
    query: queries.getJob,
    variables: { jobId, tenantId }
  });
  return (response as any).data.getJob || null;
}


/**
 * Lists jobs for a user filtered by job type.
 * 
 * @param tenantId - The tenant ID
 * @param username - The username
 * @param jobType - The job type to filter by (DD or BA)
 * @param limit - Optional limit for pagination
 * @param nextToken - Optional token for pagination
 * @returns JobConnection with items and nextToken
 * 
 * Requirement 3.1: Display all Due_Diligence_Jobs belonging to the current user
 * Requirement 4.1: Display all Borrower_Analysis_Jobs belonging to the current user
 */
export async function listJobsByUser(
  tenantId: string,
  username: string,
  jobType: JobType,
  limit?: number,
  nextToken?: string,
  archivedOnly?: boolean
): Promise<JobConnection> {
  const response = await client.graphql({
    query: queries.listJobsByUser,
    variables: { tenantId, username, jobType, limit, nextToken, archivedOnly }
  });
  return (response as any).data.listJobsByUser;
}

/**
 * Updates the status of a specific step in a job.
 * 
 * @param jobId - The job ID
 * @param tenantId - The tenant ID
 * @param jobType - The job type
 * @param step - The step to update
 * @param status - The new status
 * @returns The updated Job
 * 
 * Requirement 9.5: Persist step status changes to the database in real-time
 */
export async function updateStepStatus(
  jobId: string,
  tenantId: string,
  jobType: JobType,
  step: StepType,
  status: StepStatus
): Promise<Job> {
  const input: UpdateStepStatusInput = {
    jobId,
    tenantId,
    jobType,
    step,
    status
  };
  const response = await client.graphql({
    query: mutations.updateStepStatus,
    variables: { input }
  });
  return (response as any).data.updateStepStatus;
}

/**
 * Marks a step as completed and advances the workflow.
 * 
 * @param jobId - The job ID
 * @param tenantId - The tenant ID
 * @param step - The step to complete
 * @returns The updated Job
 */
export async function completeStep(
  jobId: string,
  tenantId: string,
  step: StepType
): Promise<Job> {
  const response = await client.graphql({
    query: mutations.completeStep,
    variables: { jobId, tenantId, step }
  });
  return (response as any).data.completeStep;
}

/**
 * Deletes a job and all associated data.
 * 
 * @param jobId - The job ID
 * @param tenantId - The tenant ID
 */
export async function deleteJob(jobId: string, tenantId: string): Promise<void> {
  await client.graphql({
    query: mutations.deleteJob,
    variables: { jobId, tenantId }
  });
}

/**
 * Archives a job by setting its status to ARCHIVED. Archived jobs are hidden
 * from list queries but preserved in DynamoDB.
 */
export async function archiveJob(jobId: string, tenantId: string): Promise<Job> {
  const response = await client.graphql({
    query: mutations.updateJobStatus,
    variables: { jobId, tenantId, status: 'ARCHIVED' }
  });
  return (response as any).data.updateJobStatus;
}

/**
 * Sets a job's status to an arbitrary JobStatus value. Used to restore
 * archived jobs back to an active status.
 */
export async function setJobStatus(
  jobId: string,
  tenantId: string,
  status: JobStatus
): Promise<Job> {
  const response = await client.graphql({
    query: mutations.updateJobStatus,
    variables: { jobId, tenantId, status }
  });
  return (response as any).data.updateJobStatus;
}

/**
 * Determines if a job can advance to a target step based on prerequisites.
 * 
 * @param job - The job
 * @param targetStep - The step to check
 * @returns true if the job can advance to the target step
 * 
 * Requirement 5.5: Prevent users from skipping ahead to steps with incomplete prerequisites
 */
export function canAdvanceToStep(job: Job, targetStep: StepType): boolean {
  switch (targetStep) {
    case StepType.DOCUMENT_PROCESSING:
      // First step is always accessible
      return true;
    case StepType.DOCUMENT_VERIFICATION:
      // Requires Document Processing to be completed
      return job.documentProcessingStatus === StepStatus.COMPLETED;
    case StepType.INVESTOR_GUIDELINE_REVIEW:
      // Requires Document Verification to be completed
      return job.documentVerificationStatus === StepStatus.COMPLETED;
    default:
      return false;
  }
}

/**
 * Determines the current active step for a job.
 * Returns the first incomplete step that has all prerequisites met.
 * 
 * @param job - The job
 * @returns The current step type
 * 
 * Requirement 9.6: Support resuming jobs from the last incomplete step
 */
export function getCurrentStep(job: Job): StepType {
  // Use job.status as the source of truth — it only changes when the
  // user explicitly clicks "Next" to advance between steps.
  switch (job.status) {
    case JobStatus.DOCUMENT_VERIFICATION:
      return StepType.DOCUMENT_VERIFICATION;
    case JobStatus.INVESTOR_GUIDELINE_REVIEW:
    case JobStatus.UNDERWRITING_GUIDELINE_REVIEW:
    case JobStatus.COMPLETED:
      return StepType.INVESTOR_GUIDELINE_REVIEW;
    case JobStatus.DOCUMENT_PROCESSING:
    default:
      return StepType.DOCUMENT_PROCESSING;
  }
}
